import { type Locale } from '@aysar/contracts';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { DEFAULT_PREFERENCES } from '@/shared/utils/preferences-storage/preferences-storage.util';
import {
  type PreferencesRoot,
  type PreferencesState,
  type ThemePreference,
} from './preferences.types';

/**
 * Theme, locale and sidebar state.
 *
 * The locale lives HERE rather than in i18next because the data layer needs it:
 * `baseQueryWithAuth` sets `Accept-Language` on every request, and reading that
 * from the i18next singleton would put an i18n import inside the store. One
 * direction of flow — the slice is the source of truth, `PreferencesProvider`
 * mirrors it into i18next and onto `<html>`, and nothing writes back.
 */
const slice = createSlice({
  name: 'preferences',
  initialState: DEFAULT_PREFERENCES,
  reducers: {
    themeChanged(state, action: PayloadAction<ThemePreference>): PreferencesState {
      return { ...state, theme: action.payload };
    },
    localeChanged(state, action: PayloadAction<Locale>): PreferencesState {
      return { ...state, locale: action.payload };
    },
    sidebarToggled(state): PreferencesState {
      return { ...state, sidebarCollapsed: !state.sidebarCollapsed };
    },
    navGroupToggled(state, action: PayloadAction<string>): PreferencesState {
      const isOpen = state.openNavGroups.includes(action.payload);
      return {
        ...state,
        openNavGroups: isOpen
          ? state.openNavGroups.filter((key) => key !== action.payload)
          : [...state.openNavGroups, action.payload],
      };
    },
    // Returns the SAME state when already open: this fires on every navigation
    // from every sidebar host, and a fresh object each time would re-render
    // everything subscribed to the preferences for nothing.
    navGroupOpened(state, action: PayloadAction<string>): PreferencesState {
      if (state.openNavGroups.includes(action.payload)) return state;
      return { ...state, openNavGroups: [...state.openNavGroups, action.payload] };
    },
  },
});

export const { themeChanged, localeChanged, sidebarToggled, navGroupToggled, navGroupOpened } =
  slice.actions;
export const preferencesReducer = slice.reducer;

export const selectThemePreference = (state: PreferencesRoot): ThemePreference =>
  state.preferences.theme;
export const selectLocale = (state: PreferencesRoot): Locale => state.preferences.locale;
export const selectSidebarCollapsed = (state: PreferencesRoot): boolean =>
  state.preferences.sidebarCollapsed;
export const selectOpenNavGroups = (state: PreferencesRoot): readonly string[] =>
  state.preferences.openNavGroups;
